import React, {useState} from "react";
import {Button, Empty, Input, List, Tabs, Timeline} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {APPLICATION_MAX_NOTE_LENGTH} from "constants/application";
import {formatDateTime} from "../common/applicationUtils";
import {PreviousApplicationsTable} from "./PreviousApplicationsTable";

const mutedTextStyle = {color: "rgba(0, 0, 0, 0.45)", fontSize: 12};

export const ApplicationHistoryTabs = ({
    type,
    application,
    notes,
    history,
    previousApplications,
    canEdit,
    onChanged
}) => {
    const {t} = useTranslation(["applications"]);
    const stores = useStore();
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleAddNote = async () => {
        setSubmitting(true);
        const result = await stores.applicationStore.addApplicationNote(type, application._id, {
            note: note.trim()
        });
        setSubmitting(false);
        if (result) {
            setNote("");
            onChanged();
        }
    };

    const historyItems = [...history].reverse();

    return (
        <Tabs defaultActiveKey="notes">
            <Tabs.TabPane
                tab={t("detail.tabs.notes", {ns: "applications", count: notes.length})}
                key="notes"
            >
                {notes.length ? (
                    <List
                        dataSource={notes}
                        renderItem={item => (
                            <List.Item key={item._id}>
                                <div style={{width: "100%"}}>
                                    <div style={{display: "flex", justifyContent: "space-between"}}>
                                        <strong>{item.createdBy?.name || "-"}</strong>
                                        <span style={mutedTextStyle}>
                                            {formatDateTime(item.createdAt)}
                                        </span>
                                    </div>
                                    <div style={{whiteSpace: "pre-wrap"}}>{item.note}</div>
                                </div>
                            </List.Item>
                        )}
                    />
                ) : (
                    <Empty description={t("detail.notes.empty", {ns: "applications"})} />
                )}
                {canEdit && (
                    <div style={{marginTop: 16}}>
                        <Input.TextArea
                            rows={3}
                            maxLength={APPLICATION_MAX_NOTE_LENGTH}
                            value={note}
                            onChange={event => setNote(event.target.value)}
                            placeholder={t("detail.notes.placeholder", {ns: "applications"})}
                        />
                        <div style={{display: "flex", justifyContent: "flex-end", marginTop: 8}}>
                            <Button
                                type="primary"
                                loading={submitting}
                                disabled={!note.trim()}
                                onClick={handleAddNote}
                            >
                                {t("detail.notes.add", {ns: "applications"})}
                            </Button>
                        </div>
                    </div>
                )}
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("detail.tabs.history", {ns: "applications"})} key="history">
                {historyItems.length ? (
                    <Timeline>
                        {historyItems.map(item => (
                            <Timeline.Item key={item._id}>
                                <div style={{fontWeight: 500}}>
                                    {t("detail.history.status", {
                                        ns: "applications",
                                        status: t(`statuses.${item.status}`, {ns: "applications"})
                                    })}
                                </div>
                                <div style={mutedTextStyle}>
                                    {item.createdBy?.name || "-"} · {formatDateTime(item.createdAt)}
                                </div>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                ) : (
                    <Empty description={t("detail.history.empty", {ns: "applications"})} />
                )}
            </Tabs.TabPane>
            <Tabs.TabPane
                tab={t("detail.tabs.previous", {
                    ns: "applications",
                    count: previousApplications.length
                })}
                key="previous"
            >
                <PreviousApplicationsTable type={type} rows={previousApplications} />
            </Tabs.TabPane>
        </Tabs>
    );
};
